import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@shadcn/components/ui/dialog';
import { Button } from '@shadcn/components/ui/button';
import { Label } from '@shadcn/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from "@shadcn/components/ui/popover";
import { Calendar } from "@shadcn/components/ui/calendar";
import { CalendarIcon, Filter, X } from 'lucide-react';
import { format } from "date-fns";
import Select from 'react-select';
import superagent from 'superagent';
import { backendUrl } from '@helpers/consts';

const SaleStateReportFilterModal = ({ 
  isOpen, 
  onClose, 
  filters, 
  onApplyFilters,
  orderSourceOptions 
}) => {
  const [tempFilters, setTempFilters] = useState(filters);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch products data from API
  const fetchProductsData = async () => {
    try {
      setLoading(true);
      const response = await superagent
        .get(`${backendUrl}/product/active-skus`)
        .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
        .set('Accept', 'application/json');
      
      if (response.body && Array.isArray(response.body)) {
        setProducts(response.body);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setTempFilters(filters);
      fetchProductsData();
    }
  }, [isOpen, filters]);

  // Generate category options from products
  const categoryOptions = useMemo(() => {
    const uniqueCategories = [...new Set(products.map(p => p.category))].filter(Boolean);
    return uniqueCategories.sort().map(category => ({
      value: category,
      label: category
    }));
  }, [products]);

  // Generate product options (filtered by selected categories)
  const productOptions = useMemo(() => {
    let filteredProducts = products;
    
    if (tempFilters.categoryNames && tempFilters.categoryNames.length > 0) {
      filteredProducts = products.filter(product => 
        tempFilters.categoryNames.includes(product.category)
      );
    }
    
    // Group by productName to avoid duplicates
    const productMap = new Map();
    filteredProducts.forEach(item => {
      if (!productMap.has(item.productName)) {
        productMap.set(item.productName, {
          _id: item._id,
          productName: item.productName,
          category: item.category,
          skus: [item.sku]
        });
      } else {
        productMap.get(item.productName).skus.push(item.sku);
      }
    });
    
    return Array.from(productMap.values()).map(product => ({
      value: product._id,
      label: product.productName,
      category: product.category
    }));
  }, [products, tempFilters.categoryNames]);

  const handleOrderSourceChange = (selectedOptions) => {
    const selectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setTempFilters(prev => ({
      ...prev,
      orderSources: selectedValues
    }));
  };

  const handleDateChange = (field, date) => {
    setTempFilters(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleCategoryChange = (selectedCategories) => {
    const categoryNames = selectedCategories ? selectedCategories.map(s => s.value) : [];
    setTempFilters(prev => ({
      ...prev,
      categoryNames,
      // Clear products if they don't belong to selected categories
      productIds: prev.productIds?.filter(productId => {
        const product = products.find(p => p._id === productId);
        return product && categoryNames.includes(product.category);
      }) || []
    }));
  };

  const handleProductChange = (selectedProducts) => {
    const productNames = selectedProducts ? selectedProducts.map(s => s.label) : [];
    
    const productIds = selectedProducts ? selectedProducts.map(s => s.value) : [];
    setTempFilters(prev => {
      // Auto-add categories of selected products
      const selectedCategories = [...new Set(selectedProducts?.map(p => p.category) || [])];
      const currentCategories = new Set(prev.categoryNames || []);
      selectedCategories.forEach(category => currentCategories.add(category));
      
      return {
        ...prev,
        productNames,
        productIds,
        categoryNames: Array.from(currentCategories)
      };
    });
  };

  const handleApply = () => {
    onApplyFilters(tempFilters);
    onClose();
  };

  const handleClear = () => {
    const clearedFilters = {
      ...tempFilters,
      categoryNames: [],
      productNames: [],
      productIds: []
    };
    setTempFilters(clearedFilters);
  };

  const hasChanges = useMemo(() => {
    return JSON.stringify(tempFilters) !== JSON.stringify(filters);
  }, [tempFilters, filters]);

  const activeFiltersCount = (tempFilters.categoryNames?.length || 0) + (tempFilters.productIds?.length || 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto z-[1500]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Sale State Report Filters
            {activeFiltersCount > 0 && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                {activeFiltersCount} active
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Order Source Section */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label>Order Sources</Label>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const allSources = orderSourceOptions.map(opt => opt.value);
                  setTempFilters(prev => ({
                    ...prev,
                    orderSources: allSources
                  }));
                }}
                className="py-0 h-6 text-xs"
              >
                Select All
              </Button>
            </div>
            <Select
              isMulti
              value={orderSourceOptions.filter(option => 
                tempFilters.orderSources?.includes(option.value)
              )}
              
              onChange={handleOrderSourceChange}
              options={orderSourceOptions}
              placeholder="Select order sources..."
              className="text-sm"
              classNamePrefix="react-select"
              maxMenuHeight={200}
            />
          </div>

          {/* Date Range Section */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              Date Range
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      {tempFilters.startDate ? (
                        format(tempFilters.startDate, "dd/MM/yyyy")
                      ) : (
                        <span className="text-muted-foreground">Select date</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[1600]" align="start">
                    <Calendar
                      mode="single"
                      selected={tempFilters.startDate}
                      onSelect={(date) => handleDateChange('startDate', date)}
                      defaultMonth={tempFilters.startDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      {tempFilters.endDate ? (
                        format(tempFilters.endDate, "dd/MM/yyyy")
                      ) : (
                        <span className="text-muted-foreground">Select date</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[1600]" align="start">
                    <Calendar
                      mode="single"
                      selected={tempFilters.endDate}
                      onSelect={(date) => handleDateChange('endDate', date)}
                      defaultMonth={tempFilters.endDate}
                      initialFocus
                      disabled={(date) => tempFilters.startDate && date < tempFilters.startDate}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          {/* Category Filter */}
          <div className="space-y-2">
            <Label>Categories</Label>
            <Select
              isMulti
              value={categoryOptions.filter(opt => tempFilters.categoryNames?.includes(opt.value))}
              onChange={handleCategoryChange}
              options={categoryOptions}
              placeholder="Select categories..."
              className="text-sm"
              classNamePrefix="react-select"
              maxMenuHeight={200}
              isLoading={loading}
            />
            {tempFilters.categoryNames?.length > 0 && (
              <p className="text-xs text-gray-500">
                {tempFilters.categoryNames.length} categor{tempFilters.categoryNames.length !== 1 ? 'ies' : 'y'} selected
              </p>
            )}
          </div>

          {/* Product Filter */}
          <div className="space-y-2">
            <Label>Products</Label>
            <Select
              isMulti
              value={productOptions.filter(opt => tempFilters.productIds?.includes(opt.value))}
              onChange={handleProductChange}
              options={productOptions}
              placeholder="Select products..."
              
              
              className="flex-1"
              menuPlacement="auto"
              menuShouldBlockScroll={false}
              menuShouldScrollIntoView={false}
              styles={{
                menu: (provided) => ({
                  ...provided,
                  maxHeight: '20vh',
                  overflowY: 'auto'
                }),
                menuList: (provided) => ({
                  ...provided,
                  maxHeight: '20vh',
                  overflowY: 'auto'
                })
              }}
            

            />
            {tempFilters.productIds?.length > 0 && (
              <p className="text-xs text-gray-500">
                {tempFilters.productIds.length} product{tempFilters.productIds.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>

          {/* Active Filters Preview */}
          {activeFiltersCount > 0 && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-sm font-medium text-gray-700">Active Filters:</h5>
                <Button 
                  onClick={handleClear}
                  variant="outline" 
                  size="sm"
                  className="text-xs"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear All
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {tempFilters.categoryNames?.map(categoryName => (
                  <span
                    key={categoryName}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800"
                  >
                    📂 {categoryName}
                  </span>
                ))}
                {tempFilters.productIds?.map(productId => {
                  const product = products.find(p => p._id === productId);
                  return product ? (
                    <span
                      key={productId}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                    >
                      📦 {product.productName}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleApply}
            disabled={!hasChanges}
          >
            Apply Filters
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaleStateReportFilterModal; 
